/**
 * What the assistant calls people and things, per platform edition. The words come from the
 * `terminology` block of the common locale files — the same files the UI reads — so the
 * assistant and the screens can never drift apart.
 */
import { LANGUAGES } from 'src/i18n/constants';
import type { PlatformEdition } from 'src/types/platform-feature';
import { EDITION_I18N_CONTEXT } from 'src/utils/platform-features';

type TermKey =
  | 'learner'
  | 'learners'
  | 'team'
  | 'team_member'
  | 'team_members'
  | 'curator'
  | 'curators'
  | 'homework'
  | 'school';
type Terminology = Partial<Record<string, string>>;

// Only the `terminology` block of each file is bundled, not the whole locale.
const TERMINOLOGY = import.meta.glob<Terminology>('/src/i18n/common-locales/*.json', {
  eager: true,
  import: 'terminology',
});

const termsOf = (language: string): Terminology =>
  TERMINOLOGY[`/src/i18n/common-locales/${language}.json`] ?? {};

/** A term in one language, with i18next's context fallback: `key_<context>`, then `key`. */
export const termFor = (edition: PlatformEdition, key: TermKey, language = 'en'): string => {
  const context = EDITION_I18N_CONTEXT[edition];
  const terms = termsOf(language);
  return (context && terms[`${key}_${context}`]) || terms[key] || key;
};

const inEveryLanguage = (edition: PlatformEdition, key: TermKey) =>
  LANGUAGES.map((language) => `${language} "${termFor(edition, key, language)}"`).join(', ');

/**
 * Prompt section with the app's words. The team words are the same on every edition; an
 * edition with its own wording also renames learners, homework and the school.
 */
export const buildTerminologySection = (edition: PlatformEdition): string => {
  const hasOwnWording = Boolean(EDITION_I18N_CONTEXT[edition]);
  const term = (key: TermKey) => inEveryLanguage(edition, key);

  const lines = [
    `- Learners — one: ${term('learner')}; many: ${term('learners')}.${hasOwnWording ? ' Never call them "students" or a translation of it.' : ''}`,
    `- The people who run the platform are its team: ${term('team')}. One of them is a team member — ${term('team_member')}; many: ${term('team_members')}. Never call them "mentors" or "employees", and never use the learners' word for them.`,
    `- A team member who checks submissions or looks after a stream or group is its curator — ${term('curator')}; many: ${term('curators')}. Owners, administrators and assistants keep their role names.`,
    ...(hasOwnWording
      ? [
          `- Homework: ${term('homework')}. The organisation: ${term('school')}.`,
        ]
      : []),
  ];

  return `

## Terminology
This platform has its own words for people. Use them exactly as the app does, in every language you answer in:
${lines.join('\n')}
Tool names, parameters and data still say "member"${hasOwnWording ? ', "student" and "homework"' : ''} — they mean these same people and things. Do not mention that to the user.`;
};
